import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import useScrollToTop from './ScrollToTop';
import { useTranslation, Trans } from 'react-i18next';
import Slider from 'react-slick';

import './about.css';

/* ─────────────────────────────────────────
   SLIDER SETTINGS
───────────────────────────────────────── */
const sliderSettings = {
  dots: true,
  infinite: true,
  speed: 600,
  slidesToShow: 3,
  slidesToScroll: 1,
  arrows: false,
  autoplay: true,
  autoplaySpeed: 3000,
  pauseOnHover: true,
  responsive: [
    { breakpoint: 1024, settings: { slidesToShow: 2 } },
    { breakpoint: 640,  settings: { slidesToShow: 1 } },
  ],
};

/* ─────────────────────────────────────────
   TOOLS SUB-COMPONENT
───────────────────────────────────────── */
const Tools = () => {
  const { t } = useTranslation('about');

  const items = [
    { img: '/images/fridge.jpg',          key: 0 },
    { img: '/images/frequenzy.jpg',       key: 1 },
    { img: '/images/binoculars (1).jpg',  key: 2 },
    { img: '/images/roof.jpg',            key: 3, fullWidth: true },
    { img: '/images/book1.jpg',           key: 4 },
  ];

  return (
    <section className="tools-section">
      <div className="tools-heading-wrap">
        <p className="section-label center">{t('tools.title')}</p>
      </div>

      <div className="tools-grid">
        {items.map(({ img, key, fullWidth }) => (
          <div className="tool-tile" key={key}>
            <img
              src={img}
              alt={t(`tools.items.${key}`)}
              style={fullWidth ? { width: '100%', height: 72, objectFit: 'cover' } : {}}
            />
            <p>{t(`tools.items.${key}`)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────── */
const About = () => {
  useScrollToTop();
  const { t } = useTranslation('about');
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.replace('#', ''));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 0);
    }
  }, [location]);

  const safariGuides = [
    {
      name:        t('guides.Kikanae.name'),
      image:       '/images/Kikanae.jpeg',
      description: t('guides.Kikanae.description'),
      contact:     '+254 720 962 905',
      email:       'kikanae@oleninisafaris.com',
    },
    {
      name:        t('guides.Weeks.name'),
      image:       '/images/Hazel.jpeg',
      description: t('guides.Weeks.description'),
      contact:     '+254 727 221 650',
      email:       'info@oleninisafaris.com',
    },
    {
      name:        t('guides.setek.name'),
      image:       '/images/setek.jpeg',
      description: t('guides.setek.description'),
      contact:     '+254 715 622 498',
      email:       'setek@oleninisafaris.com',
    },
    {
      name:        t('guides.Lawrence.name'),
      image:       '/images/lawrence.jpeg',
      description: t('guides.Lawrence.description'),
    },
    {
      name:        t('guides.Kasura.name'),
      image:       '/images/david.jpeg',
      description: t('guides.Kasura.description'),
    },
    {
      name:        t('guides.George.name'),
      image:       '/images/STAFF.jpg',
      description: t('guides.George.description'),
    },
  ];

  return (
    <div className="about-page">

      {/* ── SEO ─────────────────────────────── */}
      <Helmet>
        <title>About Olenini Safaris | Trusted Kenya Safari Tour Operator</title>
        <meta name="description" content="Olenini Tours & Safaris is a locally owned Kenya safari operator offering tailor-made wildlife experiences. Learn about our team, guides, and safari vehicles." />
        <meta name="keywords" content="Kenya safari company, safari guides Kenya, safari vehicles Kenya, tour operator Narok, locally owned safari company Kenya" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://oleninisafaris.com/about" />
        <meta property="og:title" content="About Olenini Safaris | Kenya Safari Experts" />
        <meta property="og:description" content="Discover who we are. Olenini Safaris is a Narok-based Kenya safari operator offering expert guides, quality vehicles and community impact." />
        <meta property="og:image" content="https://oleninisafaris.com/logo.png" />
        <meta property="og:url" content="https://oleninisafaris.com/about" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Olenini Safaris | Kenya Safari Experts" />
        <meta name="twitter:description" content="Learn about our local guides, safari fleet, and our mission to offer unforgettable wildlife experiences across Kenya." />
        <meta name="twitter:image" content="https://oleninisafaris.com/logo.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "TravelAgency",
            "name": "Olenini Tours & Safaris",
            "url": "https://oleninisafaris.com",
            "logo": "https://oleninisafaris.com/images/logo.png",
            "description": "Olenini Safaris is a locally owned Kenya tour company offering tailor-made wildlife safaris across top destinations like Maasai Mara, Amboseli, and Lake Naivasha.",
            "address": {
              "@type": "PostalAddress",
              "addressLocality": "Narok",
              "addressRegion": "Rift Valley",
              "addressCountry": "KE"
            },
            "areaServed": "Kenya",
            "sameAs": [
              "https://www.instagram.com/oleninisafaris/",
              "https://www.facebook.com/oleninisafaris/"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+254702965315",
              "contactType": "Customer Service"
            }
          }
        `}</script>
      </Helmet>

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section id="about" className="about-hero">
        <div className="about-hero-inner">
          <span className="about-hero-eyebrow">Olenini Tours &amp; Safaris</span>
          <h1>Who <em>We</em> Are</h1>
        </div>
      </section>

      {/* ══════════════════════════════════════
          ABOUT US INTRO
      ══════════════════════════════════════ */}
      <section className="about-intro">
        <p className="body-text">
          <Trans i18nKey="aboutUs.intro" components={{ 1: <strong /> }} />
        </p>
        <p className="body-text">
          <Trans i18nKey="aboutUs.experiences" components={{ 1: <strong /> }} />
        </p>
        <p className="body-text">
          <Trans i18nKey="aboutUs.volunteer" components={{ 1: <strong /> }} />
        </p>
        <p className="body-text">
          <Trans i18nKey="aboutUs.clinic" components={{ 1: <strong /> }} />
        </p>
      </section>

      <hr className="o-rule" />

      {/* ══════════════════════════════════════
          IMPACT MANIFESTO
      ══════════════════════════════════════ */}
      <section className="impact-manifesto">
        <div className="impact-manifesto-inner">
          <h2 className="section-heading xl center">
            {t('impact.heading')}
          </h2>

          <div className="manifesto-lines">
            <p><Trans i18nKey="impact.line1" components={{ 1: <strong /> }} /></p>
            <p>{t('impact.line2')}</p>
            <p>{t('impact.line3')}</p>
            <p className="tagline">
              <Trans i18nKey="impact.line4" components={{ 1: <em /> }} />
            </p>
            <p className="cta-line">
              <Trans i18nKey="impact.line5" components={{ 1: <strong /> }} />
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FLEET
      ══════════════════════════════════════ */}
      <section id="Olenini" className="fleet-section">

        {/* Fleet Hero Split */}
        <div className="split-hero">
          <div className="split-hero-image">
            <img src="/images/fleet2.jpeg" alt="Olenini Tours Safari fleet vehicle in African wilderness" />
          </div>
          <div className="split-hero-content">
            <h2 className="section-heading lg bronze">
              {t('fleet.heading')}
            </h2>
            <div className="o-rule" />
            <p className="body-text">
              <Trans i18nKey="fleet.paragraph" components={{ 1: <b /> }} />
            </p>
          </div>
        </div>

        {/* Safari Experience prose */}
        <div className="experience-block">
          <h2 className="section-heading lg center bronze" style={{ marginBottom: '2rem' }}>
            {t('safariExperience.heading')}
          </h2>
          <p className="body-text">
            <Trans i18nKey="safariExperience.para1" components={{ 1: <strong /> }} />
          </p>
          <p className="body-text">{t('safariExperience.para2')}</p>
          <p className="body-text">
            <Trans i18nKey="safariExperience.para3" components={{ 1: <strong /> }} />
          </p>
        </div>

        {/* Fleet photo pair */}
        <div className="fleet-gallery">
          <div className="fleet-photo">
            <img src="/images/ffleetmuddy.jpeg" alt="Olenini Safari Fleet" />
            <div className="fleet-photo-label">Safari Fleet</div>
          </div>
          <div className="fleet-photo">
            <img src="/images/carwash.jpeg" alt="Olenini Airport Transfers" />
            <div className="fleet-photo-label">Airport Transfers</div>
          </div>
        </div>

        {/* Second experience prose */}
        <div className="experience-block">
          <p className="body-text">
            <Trans i18nKey="safariExperience1.para1" components={{ 1: <strong /> }} />
          </p>
          <p className="body-text">{t('safariExperience1.para2')}</p>
          <p className="body-text">
            <Trans i18nKey="safariExperience1.para3" components={{ 1: <strong /> }} />
          </p>
        </div>

        {/* Fleet tools */}
        <Tools />
      </section>

      <hr className="o-rule" />

      {/* ══════════════════════════════════════
          STAFF
      ══════════════════════════════════════ */}
      <section id="staff" className="staff-section">

        {/* Staff Hero Split — image right */}
        <div className="split-hero reverse">
          <div className="split-hero-content">
            <h2 className="section-heading lg">
              {t('staff.heading')}
            </h2>
            <div className="o-rule" />
            <p className="body-text">
              <Trans i18nKey="staff.intro" components={{ 1: <b /> }} />
            </p>
          </div>
          <div className="split-hero-image">
            <img src="/images/staff.jpeg" alt="Olenini Tours guides and staff" />
          </div>
        </div>

        {/* Team prose */}
        <div className="team-prose">
          <h2 className="section-heading lg center bronze" style={{ marginBottom: '2rem' }}>
            {t('safariTeam.title')}
          </h2>
          <p className="body-text">
            <Trans i18nKey="safariTeam.para1" components={{ 1: <strong /> }} />
          </p>
          <p className="body-text">{t('safariTeam.para2')}</p>
          <p className="body-text">{t('safariTeam.para3')}</p>
          <p className="body-text">
            <Trans i18nKey="safariTeam.para4" components={{ 1: <strong /> }} />
          </p>
        </div>

        {/* Guide slider */}
        <div className="guides-wrapper">
          <div className="guides-heading-wrap">
            <h2 className="section-heading lg center">
              {t('safariTeam.guideHeading')}
            </h2>
          </div>

          <Slider {...sliderSettings}>
            {safariGuides.map((guide, i) => (
              <div key={i}>
                <div className="guide-card">
                  <img
                    className="guide-card-photo"
                    src={guide.image}
                    alt={guide.name}
                  />

                  <h4 className="guide-card-name">{guide.name}</h4>

                  {(guide.contact || guide.email) && (
                    <div className="guide-contact">
                      {guide.contact && (
                        <a href={`tel:${guide.contact}`} data-type="phone">
                          {guide.contact}
                        </a>
                      )}
                      {guide.contact && guide.email && (
                        <span className="contact-divider" />
                      )}
                      {guide.email && (
                        <a href={`mailto:${guide.email}`} data-type="email">
                          {guide.email}
                        </a>
                      )}
                    </div>
                  )}

                  <p className="guide-card-desc">{guide.description}</p>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>

    </div>
  );
};

export default About;